import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ShieldCheck, Users, Building2, Mail, AlertOctagon, Activity, Database,
  Radar, ClipboardList, Sparkles, Loader2, RefreshCw, Pause, Play, Send, Crown,
  Cpu, Package, ExternalLink, CheckCircle2, XCircle,
} from "lucide-react";
import AppShell from "../components/argus/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";

const SECTIONS = [
  { id: "overview", label: "Overview", Icon: Activity },
  { id: "engines",  label: "Engines",  Icon: Cpu },
  { id: "users",    label: "Users",    Icon: Users },
  { id: "orgs",     label: "Orgs",     Icon: Building2 },
  { id: "leads",    label: "Leads",    Icon: Mail },
  { id: "abuse",    label: "Abuse",    Icon: AlertOctagon },
  { id: "scans",    label: "Scans",    Icon: Radar },
  { id: "audit",    label: "Audit",    Icon: ClipboardList },
  { id: "outbox",   label: "Email",    Icon: Send },
  { id: "system",   label: "System",   Icon: Database },
];

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "—");
const niceDate = (s) => (s ? new Date(s).toLocaleString() : "—");

// ---------- Section: Overview ----------
function Overview() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setStats((await api.get("/admin/stats")).data); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  const tiles = stats ? [
    { label: "Users", value: stats.users_total, sub: `${stats.users_unverified} unverified · ${stats.users_suspended} suspended`, Icon: Users },
    { label: "Orgs",  value: stats.orgs_total,  sub: `${stats.orgs_free} free · ${stats.orgs_pro} pro · ${stats.orgs_enterprise} ent`, Icon: Building2 },
    { label: "Projects", value: stats.projects_total, sub: "across all orgs", Icon: ShieldCheck },
    { label: "Scans (24h)", value: stats.scans_24h, sub: `${stats.scans_total} all-time`, Icon: Radar },
    { label: "Findings (open critical)", value: stats.findings_critical_open, sub: `${stats.findings_total} total`, Icon: AlertOctagon },
    { label: "Design-partner leads", value: stats.leads_total, sub: `${stats.leads_new} new`, Icon: Mail },
    { label: "Abuse reports", value: stats.abuse_total, sub: `${stats.abuse_new} new`, Icon: AlertOctagon },
    { label: "Email outbox", value: stats.email_outbox_queued, sub: `${stats.email_outbox_failed} failed`, Icon: Send },
  ] : [];
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl">Owner overview</h2>
          <p className="text-ink/60 mt-1">Everything in one place. Refreshed on demand.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={busy} className="border-line bg-transparent" data-testid="admin-refresh">
          <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {!stats ? <Skeleton /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
          {tiles.map((t) => (
            <div key={t.label} className="rounded-xl border border-line bg-panel p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-ink/50">{t.label}</span>
                <t.Icon size={16} className="text-gold" />
              </div>
              <div className="font-display text-3xl text-ink">{fmt(t.value)}</div>
              <div className="text-xs text-ink/50 mt-1">{t.sub}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Section: Users ----------
function UsersSection() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const load = async () => setRows((await api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`)).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const toggleSuspend = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { suspended: !u.suspended });
      toast.success(u.suspended ? "Account restored." : "Account suspended.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search email…"
          className="bg-paper border-line max-w-xs"
          data-testid="admin-users-search"
        />
        <Button onClick={load} variant="outline" className="border-line bg-transparent">Search</Button>
      </div>
      {!rows ? <Skeleton /> : rows.length === 0 ? <Empty msg="No users." /> : (
        <div className="rounded-xl border border-line overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2.5 text-left">Email</th>
                <th className="px-4 py-2.5 text-left">Name</th>
                <th className="px-4 py-2.5 text-left">Org</th>
                <th className="px-4 py-2.5 text-left">Role</th>
                <th className="px-4 py-2.5 text-left">Plan</th>
                <th className="px-4 py-2.5 text-left">Verified</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-t border-line">
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">{u.name}</td>
                  <td className="px-4 py-3 text-ink/70">{u.org_name || "—"}</td>
                  <td className="px-4 py-3">
                    {u.role === "superadmin" ? <span className="inline-flex items-center gap-1 text-gold"><Crown size={12} /> owner</span> : u.role}
                  </td>
                  <td className="px-4 py-3 uppercase text-xs text-ink/70">{u.org_plan || "—"}</td>
                  <td className="px-4 py-3 text-xs">{u.email_verified ? <span className="text-safe">yes</span> : <span className="text-sev-medium">no</span>}</td>
                  <td className="px-4 py-3 text-xs">{u.suspended ? <span className="text-sev-high">suspended</span> : <span className="text-safe">active</span>}</td>
                  <td className="px-4 py-3 text-right">
                    {u.role !== "superadmin" && (
                      <Button size="sm" variant="outline" className="border-line bg-transparent text-xs"
                        onClick={() => toggleSuspend(u)} data-testid={`user-toggle-${u.id}`}>
                        {u.suspended ? <><Play size={12} className="mr-1" /> Restore</> : <><Pause size={12} className="mr-1" /> Suspend</>}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Section: Orgs ----------
function OrgsSection() {
  const [rows, setRows] = useState(null);
  const load = async () => setRows((await api.get("/admin/orgs")).data);
  useEffect(() => { load(); }, []);
  const setPlan = async (org, plan) => {
    try {
      await api.patch(`/admin/orgs/${org.id}`, { plan });
      toast.success(`Plan set to ${plan}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change plan.");
    }
  };
  return !rows ? <Skeleton /> : rows.length === 0 ? <Empty msg="No orgs." /> : (
    <div className="rounded-xl border border-line overflow-hidden">
      <table className="w-full text-sm" data-testid="admin-orgs-table">
        <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
          <tr>
            <th className="px-4 py-2.5 text-left">Name</th>
            <th className="px-4 py-2.5 text-left">Plan</th>
            <th className="px-4 py-2.5 text-left">Members</th>
            <th className="px-4 py-2.5 text-left">Projects</th>
            <th className="px-4 py-2.5 text-left">Scans</th>
            <th className="px-4 py-2.5 text-left">Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} className="border-t border-line">
              <td className="px-4 py-3">{o.name}</td>
              <td className="px-4 py-3">
                <Select value={o.plan} onValueChange={(v) => setPlan(o, v)}>
                  <SelectTrigger className="h-8 w-32 bg-paper border-line text-xs" data-testid={`org-plan-${o.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="px-4 py-3">{o.members}</td>
              <td className="px-4 py-3">{o.projects}</td>
              <td className="px-4 py-3">{o.scans}</td>
              <td className="px-4 py-3 text-xs text-ink/60">{niceDate(o.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Section: Leads ----------
function LeadsSection() {
  const [rows, setRows] = useState(null);
  const load = async () => setRows((await api.get("/admin/leads")).data);
  useEffect(() => { load(); }, []);
  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update.");
    }
  };
  return !rows ? <Skeleton /> : rows.length === 0 ? <Empty msg="No design-partner leads yet." /> : (
    <div className="space-y-3" data-testid="admin-leads-list">
      {rows.map((l) => (
        <div key={l.id} className="rounded-xl border border-line bg-panel p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">{l.name}</h3>
                <a className="text-gold text-sm hover:underline" href={`mailto:${l.email}`}>{l.email}</a>
                {l.status && <span className="text-[10px] uppercase tracking-wider text-ink/60 px-2 py-0.5 rounded-full border border-line">{l.status}</span>}
              </div>
              <div className="text-xs text-ink/60">
                {l.company || "—"} · {l.use_case || "—"} · {niceDate(l.received_at)}
              </div>
              {l.target_url && <div className="text-xs text-ink/60 mt-1 font-mono">{l.target_url}</div>}
              {l.note && <p className="text-sm text-ink/80 mt-2 whitespace-pre-wrap">{l.note}</p>}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button size="sm" variant="outline" className="border-line bg-transparent text-xs" onClick={() => setStatus(l, "contacted")}>Contacted</Button>
              <Button size="sm" variant="outline" className="border-line bg-transparent text-xs" onClick={() => setStatus(l, "qualified")}>Qualified</Button>
              <Button size="sm" variant="outline" className="border-line bg-transparent text-xs" onClick={() => setStatus(l, "rejected")}>Reject</Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Section: Abuse ----------
function AbuseSection() {
  const [rows, setRows] = useState(null);
  const load = async () => setRows((await api.get("/admin/abuse")).data);
  useEffect(() => { load(); }, []);
  const setStatus = async (r, status) => {
    try { await api.patch(`/admin/abuse/${r.id}`, { status }); toast.success(`Marked ${status}.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
  };
  return !rows ? <Skeleton /> : rows.length === 0 ? <Empty msg="No abuse reports." /> : (
    <div className="space-y-3" data-testid="admin-abuse-list">
      {rows.map((r) => (
        <div key={r.id} className="rounded-xl border border-line bg-panel p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs text-ink/60 mb-1">{niceDate(r.received_at)} · <span className="uppercase tracking-wider">{r.status}</span></div>
              <div className="font-mono text-sm">{r.target}</div>
              {r.reporter_email && <div className="text-xs text-ink/60 mt-1">from {r.reporter_email}</div>}
              <p className="text-sm text-ink/80 mt-2 whitespace-pre-wrap">{r.description}</p>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <Button size="sm" variant="outline" className="border-line bg-transparent text-xs" onClick={() => setStatus(r, "actioned")}>Action</Button>
              <Button size="sm" variant="outline" className="border-line bg-transparent text-xs" onClick={() => setStatus(r, "dismissed")}>Dismiss</Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Section: Scans / Audit / Outbox ----------
function ScansSection() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/scans").then((r) => setRows(r.data)); }, []);
  return !rows ? <Skeleton /> : rows.length === 0 ? <Empty msg="No scans." /> : (
    <div className="rounded-xl border border-line overflow-hidden">
      <table className="w-full text-sm" data-testid="admin-scans-table">
        <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
          <tr><th className="px-4 py-2.5 text-left">Started</th><th className="px-4 py-2.5 text-left">Scanner</th><th className="px-4 py-2.5 text-left">Status</th><th className="px-4 py-2.5 text-left">Findings</th><th className="px-4 py-2.5 text-left">Duration</th><th className="px-4 py-2.5 text-left">Org</th></tr>
        </thead>
        <tbody>{rows.map((s) => (
          <tr key={s.id} className="border-t border-line">
            <td className="px-4 py-3 text-xs">{niceDate(s.started_at)}</td>
            <td className="px-4 py-3 uppercase text-xs">{s.scanner}</td>
            <td className="px-4 py-3 text-xs">{s.status}</td>
            <td className="px-4 py-3">{s.findings_count ?? 0}</td>
            <td className="px-4 py-3 text-xs text-ink/60">{s.duration_ms ? `${s.duration_ms} ms` : "—"}</td>
            <td className="px-4 py-3 text-xs font-mono text-ink/50">{(s.org_id || "").slice(0, 8)}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function AuditSection() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/audit").then((r) => setRows(r.data)); }, []);
  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/audit/export.csv?days=30", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `argussecure-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit log exported.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't export the audit log.");
    }
  };
  return !rows ? <Skeleton /> : (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink/65">{rows.length} entries · most recent first.</p>
        <Button variant="outline" onClick={downloadCsv} className="border-line bg-transparent" data-testid="admin-audit-export-csv">
          <Database size={14} className="mr-2" /> Export last 30 days (CSV)
        </Button>
      </div>
      {rows.length === 0 ? <Empty msg="No audit entries." /> : (
        <ul className="space-y-1 text-sm" data-testid="admin-audit-list">
          {rows.map((a) => (
            <li key={a.id} className="rounded-md bg-panel border border-line px-3 py-2">
              <div className="flex items-center gap-3 text-xs text-ink/60"><span className="font-mono">{niceDate(a.at)}</span><span className="uppercase tracking-wider text-ink/80">{a.action}</span></div>
              {a.detail && Object.keys(a.detail).length > 0 && (
                <div className="text-xs text-ink/60 font-mono mt-1 break-all">{JSON.stringify(a.detail)}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OutboxSection() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/email-outbox").then((r) => setRows(r.data)); }, []);
  return !rows ? <Skeleton /> : rows.length === 0 ? <Empty msg="Outbox empty." /> : (
    <div className="rounded-xl border border-line overflow-hidden">
      <table className="w-full text-sm" data-testid="admin-outbox-table">
        <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
          <tr><th className="px-4 py-2.5 text-left">Queued</th><th className="px-4 py-2.5 text-left">To</th><th className="px-4 py-2.5 text-left">Subject</th><th className="px-4 py-2.5 text-left">Kind</th><th className="px-4 py-2.5 text-left">Provider</th><th className="px-4 py-2.5 text-left">Status</th></tr>
        </thead>
        <tbody>{rows.map((e) => (
          <tr key={e.id} className="border-t border-line">
            <td className="px-4 py-3 text-xs">{niceDate(e.queued_at)}</td>
            <td className="px-4 py-3 font-mono text-xs">{e.to}</td>
            <td className="px-4 py-3">{e.subject}</td>
            <td className="px-4 py-3 text-xs text-ink/60">{e.kind}</td>
            <td className="px-4 py-3 text-xs text-ink/60">{e.provider || "—"}</td>
            <td className="px-4 py-3 text-xs">{e.status}{e.error ? <span className="block text-sev-high">{e.error.slice(0, 80)}</span> : null}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function SystemSection() {
  const [data, setData] = useState(null);
  const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/system").then((r) => setData(r.data)); }, []);
  const rotate = async () => {
    if (pw.length < 12) { toast.error("Use at least 12 characters."); return; }
    setBusy(true);
    try { await api.post("/admin/owner/rotate-password", { new_password: pw }); toast.success("Owner password rotated."); setPw(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };
  if (!data) return <Skeleton />;
  const env = data.env || {};
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-line bg-panel p-5" data-testid="admin-system-db">
        <h3 className="font-display text-xl mb-3">Database</h3>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <dt className="text-ink/60">Name</dt><dd className="font-mono">{data.db_name}</dd>
          <dt className="text-ink/60">Collections</dt><dd>{data.collections}</dd>
          <dt className="text-ink/60">Documents</dt><dd>{fmt(data.objects)}</dd>
          <dt className="text-ink/60">Storage</dt><dd>{Math.round((data.db_size_bytes || 0) / 1024)} KB</dd>
        </dl>
      </div>
      <div className="rounded-xl border border-line bg-panel p-5" data-testid="admin-system-env">
        <h3 className="font-display text-xl mb-3">Integrations</h3>
        <ul className="text-sm space-y-1.5">
          <li><Dot ok={env.EMERGENT_LLM_KEY_set} /> Emergent LLM key (AI Fix Agent)</li>
          <li><Dot ok={env.RESEND_API_KEY_set} /> Resend API key (transactional email)</li>
          <li><Dot ok={env.APP_PUBLIC_URL_set} /> App public URL (for email links)</li>
          <li><Dot ok={env.OWNER_EMAIL_set} /> OWNER_EMAIL override</li>
          <li className="text-ink/60 text-xs pt-1">From: {env.EMAIL_FROM}</li>
        </ul>
        {!env.RESEND_API_KEY_set && (
          <p className="text-xs text-ink/60 mt-3">
            <span className="text-gold">Tip:</span> Add <code className="font-mono">RESEND_API_KEY</code> in <code className="font-mono">/app/backend/.env</code> to switch emails from outbox-only to real send. Get a free key at <a className="text-gold underline" href="https://resend.com" target="_blank" rel="noreferrer">resend.com</a>.
          </p>
        )}
      </div>
      <div className="rounded-xl border border-line bg-panel p-5">
        <h3 className="font-display text-xl mb-3">Rotate owner password</h3>
        <div className="flex gap-2 max-w-md">
          <Input
            type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="New password (12+ chars)"
            className="bg-paper border-line"
            data-testid="admin-rotate-pw"
          />
          <Button onClick={rotate} disabled={busy} className="bg-gold text-paper hover:bg-gold2 font-semibold" data-testid="admin-rotate-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Rotate"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Dot({ ok }) {
  return <span className={`inline-block w-2 h-2 rounded-full mr-2 ${ok ? "bg-safe" : "bg-sev-medium"}`} />;
}

// ---------- Section: Engines (scanner registry) ----------
function ConnectorConfigDialog({ engine, onSaved, onClose }) {
  const [values, setValues] = React.useState(() =>
    Object.fromEntries((engine.env_keys || []).map((k) => [k, ""]))
  );
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const update = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      // Strip empty values so we don't overwrite the existing stored secrets.
      const filtered = Object.fromEntries(Object.entries(values).filter(([, v]) => v && v.trim() !== ""));
      if (Object.keys(filtered).length === 0) {
        toast.error("Enter at least one credential value.");
        return;
      }
      await api.put(`/admin/engines/${engine.id}/connector`, { config: filtered });
      toast.success("Credentials saved (encrypted at rest).");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save credentials.");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setResult(null);
    try {
      const r = await api.post(`/admin/engines/${engine.id}/test`);
      setResult(r.data);
      if (r.data.ok) toast.success("Connection verified.");
      else toast.error(r.data.detail || "Test connection failed.");
      onSaved?.(); // refresh the parent list to show the new status
    } catch (e) {
      const detail = e?.response?.data?.detail || "Test connection failed.";
      setResult({ ok: false, detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove the saved credentials for ${engine.name}?`)) return;
    try {
      await api.delete(`/admin/engines/${engine.id}/connector`);
      toast.success("Credentials removed.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove credentials.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-paper/80 backdrop-blur-sm flex items-start justify-center pt-20 px-4" data-testid={`connector-dialog-${engine.id}`}>
      <div className="rounded-xl border border-line bg-panel p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-start gap-3 mb-4">
          <Cpu size={18} className="text-gold mt-1" />
          <div className="flex-1">
            <h3 className="font-display text-xl text-ink">{engine.name}</h3>
            <p className="text-xs text-ink/60 mt-1">{engine.description}</p>
          </div>
          <button onClick={onClose} className="text-ink/50 hover:text-ink text-xl" data-testid="connector-dialog-close">×</button>
        </div>
        <div className="space-y-3 mb-5">
          {(engine.env_keys || []).map((k) => (
            <div key={k}>
              <label className="text-[11px] uppercase tracking-wider text-ink/55 font-mono block mb-1">{k}</label>
              <input
                type="password"
                value={values[k] || ""}
                onChange={update(k)}
                placeholder={engine.connector_status?.configured_keys?.includes(k) ? "(configured — leave blank to keep)" : "Paste credential…"}
                className="w-full h-10 rounded-md border border-line bg-paper px-3 text-sm font-mono"
                data-testid={`connector-input-${engine.id}-${k}`}
              />
            </div>
          ))}
        </div>
        {engine.docs_url && (
          <p className="text-xs text-ink/55 mb-4">
            Where to find these: <a href={engine.docs_url} target="_blank" rel="noopener noreferrer" className="text-gold underline inline-flex items-center gap-1">{engine.name} docs <ExternalLink size={11} /></a>
          </p>
        )}
        {result && (
          <div className={`rounded-md p-3 text-xs mb-4 ${result.ok ? "bg-safe/5 border border-safe/30 text-safe" : "bg-sev-critical/5 border border-sev-critical/30 text-sev-critical"}`} data-testid={`connector-result-${engine.id}`}>
            <strong>{result.ok ? "Verified." : "Failed."}</strong> {result.detail}
          </div>
        )}
        <div className="flex flex-wrap gap-2 justify-end">
          {engine.connector_status?.configured_keys?.length > 0 && (
            <Button variant="outline" onClick={remove} className="border-sev-critical/40 text-sev-critical bg-transparent hover:bg-sev-critical/5" data-testid={`connector-remove-${engine.id}`}>
              Remove saved
            </Button>
          )}
          <Button variant="outline" onClick={save} disabled={saving} className="border-line bg-transparent" data-testid={`connector-save-${engine.id}`}>
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}Save
          </Button>
          <Button onClick={test} disabled={testing} className="bg-gold text-paper hover:bg-gold2 font-semibold" data-testid={`connector-test-${engine.id}`}>
            {testing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
            Test connection
          </Button>
        </div>
      </div>
    </div>
  );
}

function EnginesSection() {
  const [engines, setEngines] = useState(null);
  const [busy, setBusy] = useState(false);
  const [installing, setInstalling] = useState(null); // engine id currently installing
  const [editing, setEditing] = useState(null); // engine being configured
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/engines");
      setEngines(r.data.engines);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the engine registry.");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const installAll = async () => {
    setInstalling("all");
    try {
      const r = await api.post("/admin/engines/install");
      const results = r.data.results || {};
      const summary = Object.entries(results).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast.success(`Install run complete — ${summary}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Install failed.");
    } finally { setInstalling(null); }
  };

  const installOne = async (engineId) => {
    setInstalling(engineId);
    try {
      if (engineId === "codeql" || engineId === "dependency-check") {
        const r = await api.post(`/admin/engines/install-heavy?engine=${engineId}`);
        const status = r.data.status || "done";
        if (status === "installed" || status === "already-installed") toast.success(`${engineId}: ${status}.`);
        else toast.error(`${engineId}: ${r.data.detail || status}`);
      } else {
        const r = await api.post(`/admin/engines/install?engines=${engineId}`);
        const result = (r.data.results || {})[engineId];
        toast.success(`${engineId}: ${result}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't install ${engineId}.`);
    } finally { setInstalling(null); }
  };

  if (engines === null) return <Skeleton />;

  const groups = engines.reduce((acc, e) => {
    (acc[e.kind] = acc[e.kind] || []).push(e);
    return acc;
  }, {});
  const KIND_LABEL = {
    builtin: "Built-in (ships with ArgusSecure)",
    subprocess: "Open-source scanners (subprocess adapters)",
    connector: "Commercial connectors (credentials required)",
  };

  return (
    <div className="space-y-8 pb-32" data-testid="admin-engines-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-ink/70">
          {engines.length} engines registered — {engines.filter((e) => e.health.ok).length} healthy.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={installAll} disabled={installing !== null} className="border-line bg-transparent" data-testid="admin-engines-install-all">
            {installing === "all" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Package size={14} className="mr-2" />}
            Install bundled engines
          </Button>
          <Button variant="outline" onClick={load} disabled={busy} className="border-line bg-transparent" data-testid="admin-engines-refresh">
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
            Refresh
          </Button>
        </div>
      </div>
      {["builtin", "subprocess", "connector"].map((kind) => (
        <section key={kind} data-testid={`admin-engines-group-${kind}`}>
          <h3 className="font-display text-lg mb-3 text-ink">{KIND_LABEL[kind]}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(groups[kind] || []).map((e) => (
              <article key={e.id} className="rounded-xl border border-line bg-panel p-4 flex flex-col" data-testid={`admin-engine-${e.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  {e.health.ok
                    ? <CheckCircle2 size={14} className="text-safe shrink-0" data-testid={`admin-engine-${e.id}-ok`} />
                    : <XCircle size={14} className="text-sev-medium shrink-0" data-testid={`admin-engine-${e.id}-down`} />}
                  <h4 className="font-semibold text-sm text-ink">{e.name}</h4>
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-ink/40">{e.category}</span>
                </div>
                <p className="text-xs text-ink/65 leading-relaxed mt-1 flex-1">{e.description}</p>
                <dl className="text-[11px] font-mono text-ink/55 mt-3 space-y-0.5">
                  {e.health.version && <div><span className="text-ink/40">version:</span> {e.health.version}</div>}
                  {e.upstream_latest && (
                    <div className={e.update_available ? "text-gold" : "text-ink/55"}>
                      <span className="text-ink/40">upstream:</span> {e.upstream_latest}{e.update_available && " · update available"}
                    </div>
                  )}
                  {e.supply_chain && (
                    <div className="text-safe" data-testid={`admin-engine-${e.id}-supply-chain`}>
                      <span className="text-ink/40">supply-chain:</span> pinned v{e.supply_chain.pinned_version} · sha256 {e.supply_chain.expected_sha256}
                      {e.supply_chain.quarantined_archives > 0 && (
                        <span className="text-sev-critical"> · {e.supply_chain.quarantined_archives} quarantined</span>
                      )}
                    </div>
                  )}
                  {!e.health.ok && <div className="text-sev-medium" data-testid={`admin-engine-${e.id}-hint`}>{e.health.detail || e.health.install_hint}</div>}
                  {e.binary && <div><span className="text-ink/40">binary:</span> {e.binary}</div>}
                  {e.env_keys?.length > 0 && <div><span className="text-ink/40">env:</span> {e.env_keys.join(", ")}</div>}
                </dl>
                {(e.upstream_url || e.docs_url) && (
                  <div className="mt-3 flex gap-3 text-xs items-center flex-wrap">
                    {e.upstream_url && <a href={e.upstream_url} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline inline-flex items-center gap-1">Project <ExternalLink size={10} /></a>}
                    {e.docs_url && <a href={e.docs_url} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline inline-flex items-center gap-1">Docs <ExternalLink size={10} /></a>}
                    {e.kind === "subprocess" && !e.health.ok && (
                      <button
                        onClick={() => installOne(e.id)}
                        disabled={installing !== null}
                        className="ml-auto text-xs text-gold border border-gold/40 bg-gold/5 hover:bg-gold/10 rounded-full px-2 py-0.5 inline-flex items-center gap-1"
                        data-testid={`admin-engine-install-${e.id}`}
                      >
                        {installing === e.id ? <Loader2 size={10} className="animate-spin" /> : <Package size={10} />}
                        {e.id === "codeql" ? "Install (~1GB)" : e.id === "dependency-check" ? "Install (~300MB)" : "Install"}
                      </button>
                    )}
                    {e.kind === "connector" && (
                      <button
                        onClick={() => setEditing(e)}
                        className="ml-auto text-xs text-ink/70 hover:text-gold underline"
                        data-testid={`connector-configure-${e.id}`}
                      >
                        {e.connector_status?.configured_keys?.length > 0 ? "Reconfigure" : "Configure"}
                      </button>
                    )}
                  </div>
                )}
                {e.kind === "connector" && e.connector_status?.last_verified && (
                  <div className="mt-2 text-[10px] font-mono text-ink/45">
                    Last verified {new Date(e.connector_status.last_verified).toLocaleString()}
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      ))}
      {editing && <ConnectorConfigDialog engine={editing} onSaved={load} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Skeleton() {
  return <div className="h-40 rounded-xl bg-panel/40 border border-line animate-pulse" data-testid="admin-skeleton" />;
}
function Empty({ msg }) {
  return <div className="text-ink/50 italic py-12 text-center border border-dashed border-line rounded-xl">{msg}</div>;
}

// ---------- Page shell ----------
export default function Admin() {
  const { user } = useAuth();
  const [section, setSection] = useState("overview");
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "superadmin") return <Navigate to="/app" replace />;
  const Body = {
    overview: <Overview />, engines: <EnginesSection />, users: <UsersSection />, orgs: <OrgsSection />,
    leads: <LeadsSection />, abuse: <AbuseSection />, scans: <ScansSection />,
    audit: <AuditSection />, outbox: <OutboxSection />, system: <SystemSection />,
  }[section];

  return (
    <AppShell
      title={(
        <span className="inline-flex items-center gap-2">
          <ShieldCheck size={18} className="text-gold" /> Owner Console
        </span>
      )}
      actions={<span className="text-xs text-ink/50 hidden md:inline">signed in as <span className="font-mono">{user.email}</span></span>}
    >
      <div className="flex flex-wrap gap-2 mb-6 border-b border-line pb-3" data-testid="admin-tabs">
        {SECTIONS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-colors ${
              section === id ? "border-gold text-gold bg-gold/5" : "border-transparent text-ink/70 hover:text-ink hover:bg-raised"
            }`}
            data-testid={`admin-tab-${id}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      <div>{Body}</div>
    </AppShell>
  );
}
